// Update in Navbar.tsx
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../store/hooks";
import { setLanguage } from "../store/userSlice";
import logo from "@packages/assets/swann-icon.png";
import { useMemo, useState, useEffect } from "react";
import { menuItemsFunction } from "@packages/utils";
import { Button } from "@core/button";

const Navbar = ({
  isSidebarCollapsed,
  setIsSidebarCollapsed,
}: {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const menuItems = useMemo(
    () => menuItemsFunction(t, i18n.language),
    [t, i18n.language],
  );

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!isMobile) return;

      const currentScrollY = window.scrollY;
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY, isMobile]);

  const handleLanguageChange = (lang: string) => {
    dispatch(setLanguage(lang));
    i18n.changeLanguage(lang);
  };

  const shouldPreventAction = () => {
    const lastChatMessageCount = parseInt(
      localStorage.getItem("lastChatMessageCount") || "0",
      10,
    );
    const currentPath = location.pathname;
    return (
      lastChatMessageCount === 1 &&
      currentPath.startsWith("/assistants/new/empty")
    );
  };

  return (
    <>
      <header
        className={`
        h-20 bg-white dark:bg-dark-600 shadow-sm w-full flex items-center
        lg:relative lg:translate-y-0 z-[50] 
        ${isMobile ? "fixed top-0 transition-transform duration-300" : ""}
        ${isMobile && !isVisible ? "-translate-y-full" : "translate-y-0"}
      `}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between w-full px-8 lg:px-0">
          {/* Left section */}
          <div className="flex items-center">
            <Button
              bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
              rounded="lg"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="cursor-pointer"
              size="xs"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-gray-600 dark:text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </Button>

            <Link
              to="/"
              onClick={() => isMobile && setIsCollapsed(true)}
              className="flex items-center p-4 pl-0"
            >
              <img src={logo} alt="Madlen Logo" className="h-12 mx-auto" />
              <span className="text-2xl font-normal">Swan</span>
            </Link>

            {/* Navigation Menu - Hide on mobile */}
            <nav className="hidden md:flex ml-6">
              <ul className="flex space-x-2">
                {menuItems
                  .filter((item) => item.showOnNavbar !== false)
                  .map((item) => (
                    <li
                      key={item.id}
                      onMouseEnter={() => setActiveDropdown(item.id)}
                      onMouseLeave={() => setActiveDropdown(null)}
                      className="relative flex"
                    >
                      <Link
                        to={"/" + item.id}
                        onClick={(e) => {
                          if (shouldPreventAction()) {
                            e.preventDefault();
                          } else {
                            setActiveDropdown(null);
                          }
                        }}
                        className={`
                          flex items-center px-3 py-2 rounded-lg transition-colors
                          ${
                            location.pathname.includes(item.id)
                              ? "bg-primary-50 text-primary-500 dark:bg-primary-dark-800 dark:text-white"
                              : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                          }
                        `}
                      >
                        <item.icon size={18} className="mr-2" />
                        <span className="hidden lg:block text-sm">
                          {item.text}
                        </span>
                        {item.subPages && (
                          <ChevronDown
                            size={14}
                            className="ml-1 transition-transform duration-200"
                            style={{
                              transform:
                                activeDropdown === item.id
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                            }}
                          />
                        )}
                      </Link>

                      {/* Dropdown Menu */}
                      {item.subPages && (
                        <div
                          className={`absolute left-0 top-full pt-1 w-80 transition-all duration-200 z-[9999] border-2 border-gray-200 dark:border-gray-700 rounded-lg ${
                            activeDropdown === item.id
                              ? "opacity-100 scale-100 pointer-events-auto"
                              : "opacity-0 scale-95 pointer-events-none"
                          }`}
                        >
                          {item.subPages.map((subPage) => {
                            const longestMatch = item.subPages
                              .filter((sp) => location.pathname.includes(sp.id))
                              .sort((a, b) => b.id.length - a.id.length)[0]?.id;

                            if (subPage.id === "assistants/new/essay-grader") {
                              return (
                                <Button
                                  key={subPage.id}
                                  size="md"
                                  fullWidth
                                  bgColor={`${
                                    location.pathname.includes("/essay-grading")
                                      ? "bg-primary-50 dark:bg-primary-dark-800 "
                                      : "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  }`}
                                  textColor={`font-normal ${
                                    location.pathname.includes("/essay-grading")
                                      ? "text-primary-500 dark:text-white"
                                      : "text-gray-600 dark:text-gray-300"
                                  }`}
                                  className={`
                                    block first:rounded-t-lg last:rounded-b-lg py-3 ${subPage.color}
                                  `}
                                  onClick={() => {
                                    if (!shouldPreventAction()) {
                                      navigate("/essay-grading");
                                      setActiveDropdown(null);
                                    }
                                  }}
                                >
                                  <div className="flex items-center flex-1">
                                    <subPage.icon size={18} className="mr-3" />
                                    <span className="text-sm">
                                      {subPage.text}
                                    </span>
                                  </div>
                                </Button>
                              );
                            }

                            return (
                              <Link
                                key={subPage.id}
                                to={"/" + subPage.id}
                                onClick={(e) => {
                                  if (shouldPreventAction()) {
                                    e.preventDefault();
                                  } else {
                                    setActiveDropdown(null);
                                  }
                                }}
                                className={`
                                  block px-4 py-3 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                    subPage.color
                                  }
                                  flex items-center
                                  ${
                                    location.pathname.includes(subPage.id) &&
                                    subPage.id === longestMatch
                                      ? "bg-primary-50 text-primary-500 dark:bg-primary-dark-800 dark:text-white"
                                      : "bg-white dark:bg-gray-800 text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
                                  }
                                `}
                              >
                                <subPage.icon size={18} className="mr-3" />
                                {subPage.text}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
            </nav>
          </div>

          {/* Right section */}
          <div className="hidden md:flex items-center space-x-4">
            <div className="flex gap-2">
              <Button
                onClick={() => handleLanguageChange("tr")}
                bgColor={`${
                  i18n.language === "tr"
                    ? "bg-primary-50 dark:bg-primary-900"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
                textColor={`${
                  i18n.language === "tr"
                    ? "text-primary-600 dark:text-primary-300"
                    : "text-gray-600 dark:text-gray-300"
                }`}
                rounded="sm"
                size="xs"
              >
                TR
              </Button>
              <Button
                onClick={() => handleLanguageChange("en")}
                bgColor={`${
                  i18n.language === "en"
                    ? "bg-primary-50 dark:bg-primary-900"
                    : "hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
                textColor={`${
                  i18n.language === "en"
                    ? "text-primary-600 dark:text-primary-300"
                    : "text-gray-600 dark:text-gray-300"
                }`}
                rounded="sm"
                size="xs"
              >
                EN
              </Button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Navbar;
